import React, { useEffect, useState } from "react";
import { Plus, CupSoda } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { formatGNF, imgUrl } from "@/utils/format";

const SUGGEST_CATEGORIES = ["boissons", "desserts"];

/**
 * Upsell au checkout : boissons & desserts du même restaurant,
 * absents du panier, ajout en un clic.
 * Props: restaurant (du panier), cartItems, onAdd(dish)
 */
export default function CheckoutSuggestions({ restaurant, cartItems, onAdd }) {
  const { t } = useTranslation();
  const [dishes, setDishes] = useState([]);

  useEffect(() => {
    if (!restaurant?.restaurant_id) return;
    apiClient
      .get(`/restaurants/${restaurant.restaurant_id}/dishes`)
      .then(({ data }) => setDishes(Array.isArray(data) ? data : []))
      .catch(() => setDishes([]));
  }, [restaurant?.restaurant_id]);

  const inCart = new Set((cartItems || []).map((i) => i.dish_id));
  const suggestions = dishes
    .filter((d) => SUGGEST_CATEGORIES.includes(d.category) && d.available !== false && !inCart.has(d.dish_id))
    .slice(0, 8);

  if (!suggestions.length) return null;

  return (
    <section className="bg-white border border-sand rounded-2xl p-6" data-testid="checkout-suggestions">
      <h2 className="font-display text-xl font-semibold mb-1 flex items-center gap-2">
        <CupSoda className="w-5 h-5 text-brand-red" /> {t("co.suggestTitle")}
      </h2>
      <p className="text-xs text-mute mb-4">{t("co.suggestSub", { name: restaurant.name })}</p>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {suggestions.map((d) => (
          <div key={d.dish_id} data-testid={`suggestion-${d.dish_id}`} className="w-36 shrink-0 border border-sand rounded-xl overflow-hidden bg-white">
            <img src={imgUrl(d.image)} alt={d.name} className="w-full h-20 object-cover bg-elevated" />
            <div className="p-2.5">
              <div className="text-sm font-semibold truncate">{d.name}</div>
              <div className="text-xs text-mute">{formatGNF(d.price)}</div>
              <button
                type="button"
                data-testid={`suggestion-add-${d.dish_id}`}
                onClick={() => { onAdd(d); toast.success(t("co.suggestAdded", { name: d.name })); }}
                className="mt-2 w-full rounded-full bg-brand-red hover:bg-[#C81F2D] text-white text-xs font-semibold py-1.5 inline-flex items-center justify-center gap-1"
              >
                <Plus className="w-3 h-3" /> {t("co.suggestAdd")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
